import type {ReactNode} from 'react'
import chunk from 'lodash/chunk'
import includes from 'lodash/includes'
import {addMaybeClassName} from '@freckle/educator-entities/ts/common/helpers/classnames'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {TriStateCheckbox} from '@freckle/educator-materials/ts/components/tri-state-checkbox'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {
  batchSelectorBtn,
  batchSelect,
  fullWidthCol,
  optionsStyle,
  spreadOptionsInColumnsWrapper,
  spreadOptionsStyle,
} from './generic-picker.module.scss'

export type GenericPickerLayout = 'single' | 'rows' | 'columns'

type Props<Key extends number | string> = {
  options: Array<[Key, string, ReactNode]>
  selection: Array<Key>
  className?: string
  counterTitle?: string
  onClickOption: (key: Key, value: string) => void
  onClickBatchSelect?: (selectAll: boolean) => void
  layout?: GenericPickerLayout
  flexWrap?: boolean // allows label and node beside it to wrap
}

export function GenericPicker<Key extends number | string>(props: Props<Key>) {
  const {t} = useSafeTranslation()
  const {
    options,
    selection,
    counterTitle,
    onClickOption,
    onClickBatchSelect,
    layout,
    className,
    flexWrap,
  } = props

  const listItemClassName = addMaybeClassName('checkbox', className)

  const buttons = options.map(([key, value, maybeNode]) => {
    const inputId = `generic-picker-${String(key).replace(/\s+/g, '-')}-${String(value).replace(/\s+/g, '-')}`
    return (
      <li key={key} className={listItemClassName} data-test={`generic-picker-options-${key}`}>
        <Flex flexWrap={flexWrap === true ? 'wrap' : undefined}>
          <TriStateCheckbox
            id={inputId}
            value={value}
            label={value}
            onSelected={() => onClickOption(key, value)}
            state={includes(selection, key) ? 'selected' : 'unselected'}
          />
          {maybeNode}
        </Flex>
      </li>
    )
  })

  const NUM_COLUMNS = 3
  const useColumnsLayout = layout === 'columns'
  const columnChunks = useColumnsLayout && chunk(buttons, Math.ceil(buttons.length / NUM_COLUMNS))

  const counterNode =
    counterTitle !== undefined ? <div className="text-muted">{counterTitle}</div> : null

  const batchSelectNode = onClickBatchSelect ? (
    <div>
      <Button
        style="link"
        className={batchSelectorBtn}
        onClick={() => onClickBatchSelect(true)}
        dataTest="generic-picker-select-all"
      >
        {t('GENERIC_PICKER_LINK_SELECT_ALL')}
      </Button>
      <Button
        style="link"
        className={batchSelectorBtn}
        onClick={() => onClickBatchSelect(false)}
        dataTest="generic-picker-select-none"
      >
        {t('GENERIC_PICKER_LINK_SELECT_NONE')}
      </Button>
    </div>
  ) : null

  return (
    <Flex flexWrap="wrap">
      <div className={fullWidthCol}>
        <div className={batchSelect}>
          {batchSelectNode}
          {counterNode}
        </div>
        {layout === 'columns' && Array.isArray(columnChunks) ? (
          <div className={spreadOptionsInColumnsWrapper} data-test="generic-picker-options">
            {columnChunks.map((chunk, i) => (
              <ul key={i} className={optionsStyle}>
                {chunk}
              </ul>
            ))}
          </div>
        ) : (
          <ul
            className={`${optionsStyle} ${layout === 'rows' ? spreadOptionsStyle : ''}`}
            data-test="generic-picker-options"
          >
            {buttons}
          </ul>
        )}
      </div>
    </Flex>
  )
}
